import { ObjectType } from '@/@types/graphql-types';

type Language = 'en' | 'de';
type StreamingState = 'with-streaming' | 'no-streaming';
type OfferKey =
	| 'DVD-BUY'
	| 'DVD-RENT'
	| 'DVD-BUY-RENT'
	| 'BLURAY-BUY'
	| 'BLURAY-RENT'
	| 'BLURAY-BUY-RENT'
	| 'DVD-BUY-BLURAY-BUY'
	| 'DVD-RENT-BLURAY-RENT'
	| 'DVD-BUY-BLURAY-RENT'
	| 'DVD-RENT-BLURAY-BUY'
	| 'DVD-BUY-RENT-BLURAY-BUY'
	| 'DVD-BUY-RENT-BLURAY-BUY-RENT';
type Placeholders = Record<Language, Record<StreamingState, Record<OfferKey, string>>>;

const movieTextPlaceHolderByLanguage: Placeholders = {
	en: {
		'with-streaming': {
			'DVD-BUY': 'Besides streaming, you can also buy the movie on DVD from {{offers}} and watch it anytime.',
			'DVD-RENT': 'Besides streaming, you can rent the movie on DVD from {{offers}} and watch it right away.',
			'DVD-BUY-RENT': 'Besides streaming, you can buy or rent the movie on DVD from {{offers}}.',

			'BLURAY-BUY': 'Besides streaming, the movie is available on Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'Besides streaming, you can also rent the movie on Blu-ray from {{offers}} and watch it right away.',
			'BLURAY-BUY-RENT': 'Besides streaming, you can buy or rent the movie on Blu-ray from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'Besides streaming, you can also buy the movie on DVD from {{dvd-offers}} and on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'Besides streaming, you can rent the movie on both DVD and Blu-ray from {{offers}} and watch it right away.',
			'DVD-BUY-BLURAY-RENT':
				'Besides streaming, you can buy the movie on DVD from {{dvd-offers}} and rent it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				'Besides streaming, the movie is available to rent on DVD and to buy on Blu-ray from {{offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Besides streaming, you can buy or rent the movie on DVD from {{dvd-offers}}, and also buy it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Besides streaming, you can buy or rent the movie on DVD and Blu-ray from {{offers}}.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Right now, {{title}} isn’t available to stream online. Hit the bell and we’ll let you know the moment it’s available. If you don’t want to wait, you can already buy the DVD from {{offers}}.',
			'DVD-RENT':
				'At the moment, there’s no streaming offer for {{title}}. Tap the bell and we’ll notify you as soon as it’s online. Until then, you can rent the DVD from {{offers}} and watch it right away.',
			'DVD-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Turn on the bell to get notified as soon as it is. In the meantime, you can buy or rent the DVD today from {{offers}}.',

			'BLURAY-BUY':
				'Right now, there’s no streaming offer for {{title}}. Just hit the bell and we’ll let you know as soon as it’s available online. If you want to watch immediately, you can buy the Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell and you’ll get a notification as soon as streaming is possible. Until then, you can rent the Blu-ray from {{offers}}.',
			'BLURAY-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Set the bell and we’ll notify you as soon as it’s ready to stream. In the meantime, you can buy or rent the Blu-ray today from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} isn’t available to stream right now. Hit the bell and we’ll let you know as soon as streaming starts. If you want to watch it sooner, you can buy the DVD from {{dvd-offers}} and the Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell to get notified as soon as it’s available. For now, you can rent it on DVD and Blu-ray from {{offers}} and watch it right away.',
			'DVD-BUY-BLURAY-RENT':
				'Right now, there are no streaming offers for {{title}}. Activate the bell and we’ll notify you once it’s available online. Until then, you can buy the DVD from {{dvd-offers}} or rent the Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				"{{title}} currently can't be streamed online. With the bell, you’ll know once a stream is available. In the meantime, you can rent the DVD from {{dvd-offers}} or buy the Blu-ray from {{blu-ray-offers}}.",
			'DVD-BUY-RENT-BLURAY-BUY':
				'You can’t stream {{title}} just yet. Hit the bell and we’ll notify you as soon as it’s available. You can already buy or rent the DVD from {{dvd-offers}} and also buy the Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'At the moment, {{title}} isn’t available online. Tap the bell to get notified as soon as it can be streamed. Until then, you can buy or rent it on DVD and Blu-ray from {{offers}}.',
		},
	},
	de: {
		'with-streaming': {
			'DVD-BUY':
				'Abgesehen vom Streaming kannst Du den Film auch auf DVD bei {{offers}} kaufen und jederzeit anschauen.',
			'DVD-RENT':
				'Abgesehen vom Streaming kannst Du den Film auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'Abgesehen vom Streaming kannst Du den Film auch auf DVD bei {{offers}} kaufen oder leihen.',

			'BLURAY-BUY': 'Neben dem Streaming ist der Film auf Blu-ray bei {{offers}} verfügbar.',
			'BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du den Film auch auf Blu-ray bei {{offers}} leihen und direkt ansehen.',
			'BLURAY-BUY-RENT':
				'Neben dem Streaming hast Du die Möglichkeit, den Film auf Blu-ray bei {{offers}} zu erwerben oder auszuleihen.',

			'DVD-BUY-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du den Film auch auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du den Film sowohl auf DVD als auch auf Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du den Film auf DVD bei {{dvd-offers}} kaufen und auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Neben dem Streaming ist der Film auf DVD zum Ausleihen und auf Blu-ray bei {{offers}} zum Kaufen verfügbar.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du den Film auf DVD bei {{dvd-offers}} kaufen oder leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Abgesehen vom Streaming kannst Du den Film auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Im Moment kannst Du {{title}} noch nicht online streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, sobald der Film im Streaming verfügbar ist. Wenn Du nicht warten willst, kannst Du {{title}} jetzt schon auf DVD bei {{offers}} kaufen.',
			'DVD-RENT':
				'Zurzeit gibt es für {{title}} noch kein Streaming-Angebot. Mit der Glocke benachrichtigen wir Dich, sobald der Titel online zu streamen ist. Bis dahin kannst Du den Film auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'{{title}} ist aktuell nicht im Online-Streaming verfügbar. Aktiviere die Glocke, dann erfährst Du sofort, wenn der Titel online zu streamen ist. Schon heute kannst Du Dir den Film auf DVD auf {{offers}} leihen oder kaufen.',

			'BLURAY-BUY':
				'Gerade gibt es kein Streaming-Angebot für {{title}}. Klick einfach auf die Glocke und wir informieren Dich, sobald der Titel online zum Streamen verfügbar ist. Wenn Du ihn sofort sehen möchtest, kannst Du den Film auf Blu-ray bei {{offers}} kaufen.',
			'BLURAY-RENT':
				'Im Moment kannst Du {{title}} leider nicht im Stream anschauen. Mit der Glocke bekommst Du eine Nachricht, sobald Online-Streaming möglich ist. Bis dahin kannst Du {{title}} bequem auf Blu-ray bei {{offers}} leihen.',
			'BLURAY-BUY-RENT':
				'Aktuell ist {{title}} noch nicht im Online-Streaming verfügbar. Setze die Glocke und wir geben Dir Bescheid, sobald Du den Titel streamen kannst. Direkt heute kannst Du {{title}} schon auf Blu-ray bei {{offers}} kaufen oder leihen.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} ist momentan nicht im Stream verfügbar. Klick auf die Glocke und wir informieren Dich, sobald Online-Streaming startet. Wenn Du den Film vorher sehen willst, kannst Du ihn auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Derzeit kannst Du {{title}} nicht online streamen. Mit der Glocke erfährst Du sofort, wenn der Titel im Streaming verfügbar ist. Schon jetzt kannst Du ihn auf DVD und Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Im Moment gibt es keine Streaming-Angebote für {{title}}. Aktiviere die Glocke und wir benachrichtigen Dich, sobald der Titel online gestreamt werden kann. Bis dahin kannst Du den Film auf DVD bei {{dvd-offers}} kaufen oder auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Zurzeit ist {{title}} nicht im Online-Streaming verfügbar. Mit der Glocke bleibst Du auf dem Laufenden, sobald der Titel im Stream startet. Bis dahin kannst Du den Film auf DVD bei {{dvd-offers}} ausleihen oder auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'{{title}} kannst Du derzeit noch nicht streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, wenn Online-Streaming verfügbar ist. Schon heute gibt es den Film auf DVD bei {{dvd-offers}} zum Kaufen oder Leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} zu kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Im Moment ist {{title}} online nicht verfügbar. Mit der Glocke erfährst Du direkt, wenn der Titel gestreamt werden kann. Bis dahin kannst Du ihn auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
	},
};

const seasonTextPlaceHolderByLanguage: Placeholders = {
	en: {
		'with-streaming': {
			'DVD-BUY': 'Besides streaming, you can also buy the season on DVD from {{offers}} and watch it anytime.',
			'DVD-RENT':
				'Besides streaming, you can rent the season on DVD from {{offers}} and start watching right away.',
			'DVD-BUY-RENT': 'Besides streaming, you can buy or rent the season on DVD from {{offers}}.',

			'BLURAY-BUY': 'Besides streaming, the season is also available on Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'Besides streaming, you can rent the season on Blu-ray from {{offers}} and watch it right away.',
			'BLURAY-BUY-RENT': 'Besides streaming, you can buy or rent the season on Blu-ray from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'Besides streaming, you can also buy the season on DVD from {{dvd-offers}} and on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'Besides streaming, you can rent the season on both DVD and Blu-ray from {{offers}} and watch it right away.',
			'DVD-BUY-BLURAY-RENT':
				'Besides streaming, you can buy the season on DVD from {{dvd-offers}} and rent it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				'Besides streaming, the season is available to rent on DVD and to buy on Blu-ray from {{offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Besides streaming, you can buy or rent the season on DVD from {{dvd-offers}}, and also buy it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Besides streaming, you can buy or rent the season on DVD and Blu-ray from {{offers}}.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Right now, {{title}} isn’t available to stream online. Hit the bell and we’ll let you know the moment the season is available. If you don’t want to wait, you can already buy it on DVD from {{offers}}.',
			'DVD-RENT':
				'At the moment, there’s no streaming offer for {{title}}. Tap the bell and we’ll notify you as soon as the season is online. Until then, you can rent it on DVD from {{offers}} and watch right away.',
			'DVD-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Turn on the bell to get notified as soon as the season is streaming. For now, you can buy or rent it on DVD from {{offers}}.',

			'BLURAY-BUY':
				'Right now, there’s no streaming offer for {{title}}. Just hit the bell and we’ll let you know as soon as the season is available online. If you want to watch immediately, you can buy it on Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell and you’ll get a notification as soon as streaming is possible. Until then, you can rent the season on Blu-ray from {{offers}}.',
			'BLURAY-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Activate the bell and we’ll notify you as soon as the season can be streamed. In the meantime, you can already buy or rent it on Blu-ray from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} isn’t available to stream right now. Hit the bell and we’ll let you know as soon as streaming starts. If you want to watch sooner, you can buy the season on DVD from {{dvd-offers}} and on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell to get notified as soon as the season is available. For now, you can rent it on DVD and Blu-ray from {{offers}} and watch right away.',
			'DVD-BUY-BLURAY-RENT':
				'Right now, there are no streaming offers for {{title}}. Activate the bell and we’ll notify you once the season is available online. Until then, you can buy the DVD from {{dvd-offers}} or rent the Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				'{{title}} isn’t currently available for streaming. With the bell, you’ll be updated as soon as the season is available. In the meantime, you can rent the DVD from {{dvd-offers}} or buy the Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'You can’t stream {{title}} just yet. Hit the bell and we’ll notify you as soon as it’s available. Already today, you can buy or rent the DVD from {{dvd-offers}} and also buy the Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'At the moment, {{title}} isn’t available online. Tap the bell to get notified as soon as the season can be streamed. Until then, you can buy or rent the DVD and Blu-ray from {{offers}}.',
		},
	},
	de: {
		'with-streaming': {
			'DVD-BUY':
				'Abgesehen vom Streaming kannst Du die Staffel auch auf DVD bei {{offers}} kaufen und jederzeit anschauen.',
			'DVD-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel auch auf DVD bei {{offers}} kaufen oder leihen.',

			'BLURAY-BUY': 'Neben dem Streaming ist die Staffel auf Blu-ray bei {{offers}} verfügbar.',
			'BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel auch auf Blu-ray bei {{offers}} leihen und direkt ansehen.',
			'BLURAY-BUY-RENT':
				'Neben dem Streaming hast Du die Möglichkeit, die Staffel auf Blu-ray bei {{offers}} zu erwerben oder auszuleihen.',

			'DVD-BUY-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du die Staffel auch auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel sowohl auf DVD als auch auf Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel auf DVD bei {{dvd-offers}} kaufen und auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Neben dem Streaming ist die Staffel auf DVD zum Ausleihen und auf Blu-ray bei {{offers}} zum Kaufen verfügbar.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du die Staffel auf DVD bei {{dvd-offers}} kaufen oder leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Abgesehen vom Streaming kannst Du die Staffel auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Im Moment kannst Du {{title}} noch nicht online streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, sobald die Staffel im Streaming verfügbar ist. Wenn Du nicht warten willst, kannst Du {{title}} jetzt schon auf DVD bei {{offers}} kaufen.',
			'DVD-RENT':
				'Zurzeit gibt es für {{title}} noch kein Streaming-Angebot. Mit der Glocke benachrichtigen wir Dich, sobald die Staffel online zu streamen ist. Bis dahin kannst Du die Staffel auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'{{title}} ist aktuell nicht im Online-Streaming verfügbar. Aktiviere die Glocke, dann erfährst Du sofort, wenn die Staffel online zu streamen ist. Schon heute kannst Du Dir die Staffel auf DVD auf {{offers}} leihen oder kaufen.',

			'BLURAY-BUY':
				'Gerade gibt es kein Streaming-Angebot für {{title}}. Klick einfach auf die Glocke und wir informieren Dich, sobald die Staffel online zum Streamen verfügbar ist. Wenn Du sie sofort sehen möchtest, kannst Du die Staffel auf Blu-ray bei {{offers}} kaufen.',
			'BLURAY-RENT':
				'Im Moment kannst Du {{title}} leider nicht im Stream anschauen. Mit der Glocke bekommst Du eine Nachricht, sobald Online-Streaming möglich ist. Bis dahin kannst Du die Staffel bequem auf Blu-ray bei {{offers}} leihen.',
			'BLURAY-BUY-RENT':
				'Aktuell ist {{title}} noch nicht im Online-Streaming verfügbar. Setze die Glocke und wir geben Dir Bescheid, sobald Du die Staffel streamen kannst. Direkt heute kannst Du {{title}} schon auf Blu-ray bei {{offers}} kaufen oder leihen.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} ist momentan nicht im Stream verfügbar. Klick auf die Glocke und wir informieren Dich, sobald Online-Streaming startet. Wenn Du die Staffel vorher sehen willst, kannst Du sie auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Derzeit kannst Du {{title}} nicht online streamen. Mit der Glocke erfährst Du sofort, wenn die Staffel im Streaming verfügbar ist. Schon jetzt kannst Du sie auf DVD und Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Im Moment gibt es keine Streaming-Angebote für {{title}}. Aktiviere die Glocke und wir benachrichtigen Dich, sobald die Staffel online gestreamt werden kann. Bis dahin kannst Du die Staffel auf DVD bei {{dvd-offers}} kaufen oder auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Zurzeit ist {{title}} nicht im Online-Streaming verfügbar. Mit der Glocke bleibst Du auf dem Laufenden, sobald die Staffel im Stream startet. Bis dahin kannst Du die Staffel auf DVD bei {{dvd-offers}} ausleihen oder auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'{{title}} kannst Du derzeit noch nicht streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, wenn Online-Streaming verfügbar ist. Schon heute gibt es die Staffel auf DVD bei {{dvd-offers}} zum Kaufen oder Leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} zu kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Im Moment ist {{title}} online nicht verfügbar. Mit der Glocke erfährst Du direkt, wenn die Staffel gestreamt werden kann. Bis dahin kannst Du sie auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
	},
};

const showTextPlaceHolderByLanguage: Placeholders = {
	en: {
		'with-streaming': {
			'DVD-BUY': 'Besides streaming, you can also buy the series on DVD from {{offers}} and watch it anytime.',
			'DVD-RENT':
				'Besides streaming, you can rent the series on DVD from {{offers}} and start watching right away.',
			'DVD-BUY-RENT': 'Besides streaming, you can buy or rent the series on DVD from {{offers}}.',

			'BLURAY-BUY': 'Besides streaming, the series is also available on Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'Besides streaming, you can rent the series on Blu-ray from {{offers}} and watch it right away.',
			'BLURAY-BUY-RENT': 'Besides streaming, you can buy or rent the series on Blu-ray from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'Besides streaming, you can also buy the series on DVD from {{dvd-offers}} and on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'Besides streaming, you can rent the series on both DVD and Blu-ray from {{offers}} and watch it right away.',
			'DVD-BUY-BLURAY-RENT':
				'Besides streaming, you can buy the series on DVD from {{dvd-offers}} and rent it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				'Besides streaming, the series is available to rent on DVD and to buy on Blu-ray from {{offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Besides streaming, you can buy or rent the series on DVD from {{dvd-offers}}, and also buy it on Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Besides streaming, you can buy or rent the series on DVD and Blu-ray from {{offers}}.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Right now, {{title}} isn’t available to stream online. Hit the bell and we’ll let you know the moment the series is available. If you don’t want to wait, you can already buy it on DVD from {{offers}}.',
			'DVD-RENT':
				'At the moment, there’s no streaming offer for {{title}}. Tap the bell and we’ll notify you as soon as the series is online. Until then, you can rent it on DVD from {{offers}} and watch right away.',
			'DVD-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Turn on the bell to get notified as soon as the series is streaming. For now, you can buy or rent it on DVD from {{offers}}.',

			'BLURAY-BUY':
				'Right now, there’s no streaming options for {{title}}. Just hit the bell and we’ll let you know as soon as the series is available online. If you want to watch immediately, you can buy it on Blu-ray from {{offers}}.',
			'BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell and you’ll get a notification as soon as streaming is possible. Until then, you can rent the series on Blu-ray from {{offers}}.',
			'BLURAY-BUY-RENT':
				'{{title}} isn’t currently available for online streaming. Set the bell and we’ll notify you as soon as the series can be streamed. In the meantime, you can already buy or rent it on Blu-ray from {{offers}}.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} isn’t available to stream right now. Hit the bell and we’ll let you know as soon as streaming starts. If you want to watch sooner, you can buy the series on DVD from {{dvd-offers}} and on Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-RENT':
				'At the moment, you can’t stream {{title}} online. Tap the bell to get notified as soon as the series is available. For now, you can rent it on DVD and Blu-ray from {{offers}} and watch right away.',
			'DVD-BUY-BLURAY-RENT':
				'Right now, there are no streaming offers for {{title}}. Activate the bell and we’ll notify you once the series is available online. Until then, you can buy the DVD from {{dvd-offers}} or rent the Blu-ray from {{blu-ray-offers}}.',
			'DVD-RENT-BLURAY-BUY':
				'{{title}} isn’t currently available for online streaming. With the bell, you’ll stay updated as soon as the series goes live. In the meantime, you can rent the DVD from {{dvd-offers}} or buy the Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'You can’t stream {{title}} just yet. Hit the bell and we’ll notify you as soon as it’s available. Already today, you can buy or rent the DVD from {{dvd-offers}} and also buy the Blu-ray from {{blu-ray-offers}}.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'At the moment, {{title}} isn’t available online. Tap the bell to get notified as soon as the series can be streamed. Until then, you can buy or rent it on DVD and Blu-ray from {{offers}}.',
		},
	},
	de: {
		'with-streaming': {
			'DVD-BUY':
				'Abgesehen vom Streaming kannst Du die Serie auch auf DVD bei {{offers}} kaufen und jederzeit anschauen.',
			'DVD-RENT':
				'Abgesehen vom Streaming kannst Du die Serie auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'Abgesehen vom Streaming kannst Du die Serie auch auf DVD bei {{offers}} kaufen oder leihen.',

			'BLURAY-BUY': 'Neben dem Streaming ist die Serie auf Blu-ray bei {{offers}} verfügbar.',
			'BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Serie auch auf Blu-ray bei {{offers}} leihen und direkt ansehen.',
			'BLURAY-BUY-RENT':
				'Neben dem Streaming hast Du die Möglichkeit, die Serie auf Blu-ray bei {{offers}} zu erwerben oder auszuleihen.',

			'DVD-BUY-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du die Serie auch auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Serie sowohl auf DVD als auch auf Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Abgesehen vom Streaming kannst Du die Serie auf DVD bei {{dvd-offers}} kaufen und auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Neben dem Streaming ist die Serie auf DVD zum Ausleihen und auf Blu-ray bei {{offers}} zum Kaufen verfügbar.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'Abgesehen vom Streaming kannst Du die Serie auf DVD bei {{dvd-offers}} kaufen oder leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Abgesehen vom Streaming kannst Du die Serie auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
		'no-streaming': {
			'DVD-BUY':
				'Im Moment kannst Du {{title}} noch nicht online streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, sobald die Serie im Streaming verfügbar ist. Wenn Du nicht warten willst, kannst Du {{title}} jetzt schon auf DVD bei {{offers}} kaufen.',
			'DVD-RENT':
				'Zurzeit gibt es für {{title}} noch kein Streaming-Angebot. Mit der Glocke benachrichtigen wir Dich, sobald die Serie online zu streamen ist. Bis dahin kannst Du die Serie auf DVD bei {{offers}} ausleihen und direkt anschauen.',
			'DVD-BUY-RENT':
				'{{title}} ist aktuell nicht im Online-Streaming verfügbar. Aktiviere die Glocke, dann erfährst Du sofort, wenn die Serie online zu streamen ist. Schon heute kannst Du Dir die Serie auf DVD auf {{offers}} leihen oder kaufen.',

			'BLURAY-BUY':
				'Gerade gibt es kein Streaming-Angebot für {{title}}. Klick einfach auf die Glocke und wir informieren Dich, sobald die Serie online zum Streamen verfügbar ist. Wenn Du sie sofort sehen möchtest, kannst Du die Serie auf Blu-ray bei {{offers}} kaufen.',
			'BLURAY-RENT':
				'Im Moment kannst Du {{title}} leider nicht im Stream anschauen. Mit der Glocke bekommst Du eine Nachricht, sobald Online-Streaming möglich ist. Bis dahin kannst Du {{title}} bequem auf Blu-ray bei {{offers}} leihen.',
			'BLURAY-BUY-RENT':
				'Aktuell ist {{title}} noch nicht im Online-Streaming verfügbar. Setze die Glocke und wir geben Dir Bescheid, sobald Du die Serie streamen kannst. Direkt heute kannst Du {{title}} schon auf Blu-ray bei {{offers}} kaufen oder leihen.',

			'DVD-BUY-BLURAY-BUY':
				'{{title}} ist momentan nicht im Stream verfügbar. Klick auf die Glocke und wir informieren Dich, sobald Online-Streaming startet. Wenn Du die Serie vorher sehen willst, kannst Du sie auf DVD bei {{dvd-offers}} und auf Blu-ray bei {{blu-ray-offers}} kaufen.',
			'DVD-RENT-BLURAY-RENT':
				'Derzeit kannst Du {{title}} nicht online streamen. Mit der Glocke erfährst Du sofort, wenn die Serie im Streaming verfügbar ist. Schon jetzt kannst Du sie auf DVD und Blu-ray bei {{offers}} leihen und direkt anschauen.',
			'DVD-BUY-BLURAY-RENT':
				'Im Moment gibt es keine Streaming-Angebote für {{title}}. Aktiviere die Glocke und wir benachrichtigen Dich, sobald die Serie online gestreamt werden kann. Bis dahin kannst Du sie auf DVD bei {{dvd-offers}} kaufen oder auf Blu-ray bei {{blu-ray-offers}} leihen.',
			'DVD-RENT-BLURAY-BUY':
				'Zurzeit ist {{title}} nicht im Online-Streaming verfügbar. Mit der Glocke bleibst Du auf dem Laufenden, sobald die Serie im Stream startet. Bis dahin kannst Du sie auf DVD bei {{offers}} ausleihen oder auf Blu-ray bei {{offers}} kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY':
				'{{title}} kannst Du derzeit noch nicht streamen. Klick auf die Glocke und wir sagen Dir sofort Bescheid, wenn Online-Streaming verfügbar ist. Schon heute gibt es die Serie auf DVD bei {{dvd-offers}} zum Kaufen oder Leihen und zusätzlich auf Blu-ray bei {{blu-ray-offers}} zu kaufen.',
			'DVD-BUY-RENT-BLURAY-BUY-RENT':
				'Im Moment ist {{title}} online nicht verfügbar. Mit der Glocke erfährst Du direkt, wenn die Serie gestreamt werden kann. Bis dahin kannst Du sie auf DVD und Blu-ray bei {{offers}} kaufen oder leihen.',
		},
	},
};

export function getDvdBluraySpinningTextPlaceHolder(objectType: ObjectType) {
	switch (objectType) {
		case ObjectType.ShowSeason:
			return seasonTextPlaceHolderByLanguage;
		case ObjectType.Show:
			return showTextPlaceHolderByLanguage;
		default:
			return movieTextPlaceHolderByLanguage;
	}
}
